import * as React from 'react';
import {useSelector} from 'react-redux';

import {Button} from 'shells/button/button';
import {CloseButton} from 'shells/button/close-button/close-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {colors} from 'shells/colors/colors';
import {InvoiceIcon} from 'shells/icon/svg/invoice-icon';
import {QuoteIcon} from 'shells/icon/svg/quote-icon';
import {Popover} from 'shells/popover/popover';
import {Body} from 'shells/typography/body';

import {useDismissibleUserSetting} from '../../../util/hooks/use-dismissible-user-setting';

import {getOriginSalesDocumentType} from '../email-composer-selectors';
import {EmailRelatedLeadContext} from '../email-related-lead-context';

import {useGetEmailableSalesDocumentsByLeadIds} from './graphql/hooks';
import {getSalesDocumentVariantFromType} from './helpers';

import styles from './insert-quote-link-button.module.css';

const SETTING_HAS_DISMISSED_EMAIL_EDITOR_QUOTE_LINK_INFO =
    'cuehide_has_dismissed_email_editor_quote_link_info';

type LinkButtonProps = {
    onClick: () => void;
    IconComponent: React.ComponentType<any>;
    text: string;
};

const LinkButton = (props: LinkButtonProps) => {
    const {onClick, IconComponent, text} = props;

    return (
        <Button isFullWidth={true} fitContent={true} onClick={onClick} as='div'>
            <div className='flex align-center justify-center gap-8'>
                <div className={styles['quote-link-button-icon']}>
                    <IconComponent size={14} fill={colors.greyDk} />
                </div>
                <div className={styles['quote-link-button-text']}>{text}</div>
            </div>
        </Button>
    );
};

type Props = {
    onInsertLink: (link: string) => void;
};

export const InsertQuoteLinkButton = (props: Props) => {
    const originSalesDocumentType = useSelector(getOriginSalesDocumentType);
    const [shouldDismissOnUnmount, setShouldDismissOnUnmount] = React.useState(false);
    const [shouldShowQuoteLinkInfo, setShouldShowQuoteLinkInfo] = React.useState(true);
    const relatedLead = React.useContext(EmailRelatedLeadContext);
    const relatedLeadId = relatedLead && relatedLead.id;
    const quoteLinkButtonRef = React.useRef(null);
    const dismissStateRef = React.useRef(false);
    const {salesDocuments} = useGetEmailableSalesDocumentsByLeadIds(
        relatedLeadId ? [relatedLeadId] : [],
        getSalesDocumentVariantFromType(originSalesDocumentType)
    );
    const {shouldShow: shouldShow, onDismiss: onDismissQuoteLinkInfo} = useDismissibleUserSetting(
        SETTING_HAS_DISMISSED_EMAIL_EDITOR_QUOTE_LINK_INFO
    );

    React.useEffect(() => {
        setShouldShowQuoteLinkInfo(shouldShow);
    }, [shouldShow]);

    React.useEffect(() => {
        dismissStateRef.current = shouldDismissOnUnmount;
    }, [shouldDismissOnUnmount]);

    React.useEffect(() => {
        return () => {
            if (dismissStateRef.current && onDismissQuoteLinkInfo) {
                onDismissQuoteLinkInfo();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!relatedLeadId || !salesDocuments || salesDocuments.length === 0) {
        return null;
    }

    const quote =
        salesDocuments && salesDocuments.find((document) => document.__typename === 'Quote');
    const invoice =
        salesDocuments && salesDocuments.find((document) => document.__typename === 'Invoice');
    const quoteLink = quote && quote.shareableViewInBrowserLink;
    const invoiceLink = invoice && invoice.shareableViewInBrowserLink;

    return (
        <div ref={quoteLinkButtonRef} className={styles['quote-link-button']}>
            {quoteLink ? (
                <LinkButton
                    onClick={() => props.onInsertLink(quoteLink)}
                    IconComponent={QuoteIcon}
                    text='Insert quote link'
                />
            ) : null}
            {invoiceLink ? (
                <LinkButton
                    onClick={() => props.onInsertLink(invoiceLink)}
                    IconComponent={InvoiceIcon}
                    text='Insert invoice link'
                />
            ) : null}
            {shouldShowQuoteLinkInfo && quoteLinkButtonRef.current && (
                <Popover
                    location='top'
                    anchor={quoteLinkButtonRef.current}
                    disableFocusTrapZone={true}
                >
                    <div className={styles['quote-link-info-popover']}>
                        <div className='flex flex-dir-col pb-8'>
                            <div className='flex'>
                                <Body color='dark' size='small'>
                                    Write your email or select a template and click this link to
                                    insert your quote or invoice link!
                                </Body>
                                <div className={styles['quote-link-info-popover-close-button']}>
                                    <CloseButton
                                        onClick={() => setShouldShowQuoteLinkInfo(false)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='flex align-self-end'>
                            <Checkbox
                                // @ts-expect-error
                                name='quoteLinkInfo'
                                label='Don’t show this again'
                                checked={shouldDismissOnUnmount}
                                onChange={() => setShouldDismissOnUnmount(!shouldDismissOnUnmount)}
                            />
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
};
